import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileText,
  User,
  Gavel,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  useReviewConflicts,
  useResolveConflict,
  useDetectConflicts,
} from '@/features/review/hooks/useReview';
import type { ReviewConflict } from '@/features/review/types';

// =========================================
// Props
// =========================================

interface ConflictResolverProps {
  reviewId: string;
  phase: string;
  canResolve: boolean;
}

// =========================================
// ConflictResolver Component
// =========================================

export function ConflictResolver({
  reviewId,
  phase,
  canResolve,
}: ConflictResolverProps) {
  const { data: conflicts, isLoading } = useReviewConflicts(reviewId);
  const resolveConflict = useResolveConflict();
  const detectConflicts = useDetectConflicts();

  const [resolveDialog, setResolveDialog] = useState<ReviewConflict | null>(
    null
  );

  const phaseConflicts = conflicts?.filter((c) => c.phase === phase) ?? [];
  const unresolvedCount = phaseConflicts.filter((c) => !c.resolved).length;
  const resolvedCount = phaseConflicts.filter((c) => c.resolved).length;

  const handleDetectConflicts = async () => {
    try {
      const count = await detectConflicts.mutateAsync({
        reviewId,
        phase,
      });
      if (count > 0) {
        toast.warning(`${count} new conflict(s) detected`);
      } else {
        toast.success('No conflicts detected');
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to detect conflicts'
      );
    }
  };

  const handleResolve = async (
    conflict: ReviewConflict,
    decision: 'include' | 'exclude'
  ) => {
    try {
      await resolveConflict.mutateAsync({
        conflict_id: conflict.id,
        final_decision: decision,
      });
      setResolveDialog(null);
      toast.success('Conflict resolved');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to resolve conflict'
      );
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="h-16 animate-pulse bg-slate-100 rounded-lg"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with detect button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          <h3 className="text-sm font-semibold text-slate-900">
            Conflicts
          </h3>
          {unresolvedCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {unresolvedCount} unresolved
            </Badge>
          )}
          {resolvedCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {resolvedCount} resolved
            </Badge>
          )}
        </div>
        {canResolve && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleDetectConflicts}
            disabled={detectConflicts.isPending}
          >
            {detectConflicts.isPending ? 'Detecting...' : 'Detect Conflicts'}
          </Button>
        )}
      </div>

      {/* Conflict list */}
      {phaseConflicts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <CheckCircle2 className="h-8 w-8 text-emerald-300 mb-2" />
            <p className="text-sm text-slate-500">
              No conflicts detected for this phase
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {phaseConflicts.map((conflict) => (
            <Card
              key={conflict.id}
              className={cn(
                'transition-all',
                conflict.resolved
                  ? 'border-slate-200 bg-slate-50'
                  : 'border-orange-200 bg-orange-50'
              )}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between gap-4">
                  {/* Source info */}
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText
                      className={cn(
                        'h-4 w-4 shrink-0',
                        conflict.resolved
                          ? 'text-slate-400'
                          : 'text-orange-500'
                      )}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {conflict.source?.title ??
                          conflict.source?.file_name ??
                          'Unknown source'}
                      </p>
                      {/* Show reviewer decisions */}
                      <div className="flex gap-2 mt-1">
                        {conflict.decisions?.map((d) => (
                          <div
                            key={d.id}
                            className="flex items-center gap-1 text-xs"
                          >
                            <User className="h-3 w-3 text-slate-400" />
                            <span className="text-slate-500">
                              {d.reviewer?.full_name ?? d.reviewer?.email}:
                            </span>
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-xs px-1.5 py-0',
                                d.decision === 'include' &&
                                  'text-emerald-600 border-emerald-200',
                                d.decision === 'exclude' &&
                                  'text-rose-600 border-rose-200',
                                d.decision === 'maybe' &&
                                  'text-amber-600 border-amber-200'
                              )}
                            >
                              {d.decision}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Resolve / status */}
                  <div className="shrink-0">
                    {conflict.resolved ? (
                      <Badge
                        variant="secondary"
                        className={cn(
                          'text-xs gap-1',
                          conflict.final_decision === 'include'
                            ? 'text-emerald-600'
                            : 'text-rose-600'
                        )}
                      >
                        <Gavel className="h-3 w-3" />
                        {conflict.final_decision}
                      </Badge>
                    ) : canResolve ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => setResolveDialog(conflict)}
                      >
                        Resolve
                      </Button>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-xs text-orange-600 border-orange-200"
                      >
                        Pending
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialog !== null}
        onOpenChange={(open) => !open && setResolveDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="h-5 w-5 text-indigo-600" />
              Resolve Conflict
            </DialogTitle>
            <DialogDescription>
              Reviewers disagreed on{' '}
              <span className="font-medium">
                {resolveDialog?.source?.title ??
                  resolveDialog?.source?.file_name}
              </span>
              . Make the final decision.
            </DialogDescription>
          </DialogHeader>

          {resolveDialog?.decisions && (
            <div className="space-y-2 py-2">
              <p className="text-sm font-medium text-slate-700">
                Reviewer decisions:
              </p>
              {resolveDialog.decisions.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between rounded-lg border p-2"
                >
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-700">
                      {d.reviewer?.full_name ?? d.reviewer?.email}
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-xs',
                      d.decision === 'include' &&
                        'text-emerald-600 border-emerald-200',
                      d.decision === 'exclude' &&
                        'text-rose-600 border-rose-200',
                      d.decision === 'maybe' &&
                        'text-amber-600 border-amber-200'
                    )}
                  >
                    {d.decision}
                  </Badge>
                </div>
              ))}
              {resolveDialog.decisions.some((d) => d.reason) && (
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-medium text-slate-700">
                    Reasons:
                  </p>
                  {resolveDialog.decisions
                    .filter((d) => d.reason)
                    .map((d) => (
                      <p
                        key={d.id}
                        className="text-xs text-slate-500 italic"
                      >
                        {d.reviewer?.full_name ?? d.reviewer?.email}:{' '}
                        &quot;{d.reason}&quot;
                      </p>
                    ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setResolveDialog(null)}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() =>
                resolveDialog && handleResolve(resolveDialog, 'exclude')
              }
              disabled={resolveConflict.isPending}
            >
              <XCircle className="mr-1 h-4 w-4" />
              Exclude
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() =>
                resolveDialog && handleResolve(resolveDialog, 'include')
              }
              disabled={resolveConflict.isPending}
            >
              <CheckCircle2 className="mr-1 h-4 w-4" />
              Include
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
